/**
 * Inline citation chips for rendered AI Markdown.
 *
 * Shared by the chat bubble (MessageBubble) and the quick-write document panel
 * (ArtifactPanel) through `useCitationMarkdown`, so a citation looks and opens
 * the same way wherever the user meets it. Clicking one opens the document
 * preview in the 'chat' context.
 */
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { SourceHighlightData } from "@/stores/assistant-store"
import { usePreviewStore } from "@/stores/preview-store"
import { resolveCitationTargetPage, toPrecisePreviewHighlight } from "@/lib/citation-preview"

// ===================
// Citation Button (inline superscript)
// ===================

interface CitationButtonProps {
  displayIndex: number           // Sequential number to show in UI (e.g., 4)
  originalIndex: number          // Backend source array index (e.g., 5)
  documentId: string
  documentName: string
  pageNumber: number | null
  snippet?: string
  quote?: string
  highlightData?: SourceHighlightData
}

// ===================
// Pending Citation Chip (streaming placeholder)
// ===================
//
// Mid-stream a citation marker has no honest number yet: sources arrive with
// turn completion and are then renumbered in order of first use. Rendering the
// model's raw index would show a figure that silently reshuffles at the end,
// so stream a neutral placeholder and let it resolve into the real, clickable
// citation once the sources land.

export function PendingCitationChip() {
  return (
    <span
      role="img"
      aria-label="Source pending"
      title="Resolving source…"
      className="inline-flex items-center justify-center text-[10px] font-semibold text-muted-foreground bg-muted rounded-full ml-0.5 px-1 py-0.5 align-super leading-none"
    >
      &hellip;
    </span>
  )
}

// ===================
// Library Citation Button (inline superscript with L prefix)
// ===================

interface LibraryCitationButtonProps {
  displayIndex: number
  documentId: string
  documentName: string
  pageNumber: number | null
  snippet?: string | undefined
  quote?: string
  highlightData?: SourceHighlightData
}

export function LibraryCitationButton({
  displayIndex,
  documentId,
  documentName,
  pageNumber,
  snippet,
  quote,
  highlightData,
}: LibraryCitationButtonProps) {
  const handleClick = () => {
    if (!documentId || !documentName) return

    const precise = toPrecisePreviewHighlight(highlightData, quote ?? null, `lib-${documentId}-${displayIndex}`)
    const targetPage = resolveCitationTargetPage(
      precise?.highlight ? { highlight: precise.highlight } : undefined,
      pageNumber,
    )

    void usePreviewStore.getState().openPreview({
      documentId,
      documentName,
      ...(targetPage != null && { page: targetPage }),
      context: 'chat',
      citationLabel: `Library [L${displayIndex}]`,
      ...(precise?.highlight ? { highlight: precise.highlight } : {}),
      ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
    })
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            className="inline-flex items-center justify-center text-[10px] font-semibold text-[hsl(var(--ai-accent-text))] bg-[hsl(var(--brand-soft))] hover:bg-[hsl(var(--brand-soft-strong))] rounded-full ml-0.5 px-1 py-0.5 align-super leading-none transition-colors cursor-pointer"
            aria-label={`Library source ${displayIndex}: ${documentName}`}
          >
            {/* No icon: the "L" already says library, and a glyph this small in
                the middle of a sentence reads as noise. */}
            L{displayIndex}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px] bg-popover text-popover-foreground border shadow-md">
          <p className="text-xs font-medium">{documentName}</p>
          {pageNumber ? (
            <p className="text-meta">Page {pageNumber}</p>
          ) : null}
          {(quote ?? snippet) && (
            <p className="text-meta mt-1 line-clamp-3">
              &ldquo;{quote ?? snippet}&rdquo;
            </p>
          )}
          <p className="text-[10px] text-muted-foreground mt-1">Library document</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function CitationButton({
  displayIndex,
  originalIndex,
  documentId,
  documentName,
  pageNumber,
  snippet,
  quote,
  highlightData: sourceHighlightData,
}: CitationButtonProps) {
  const handleClick = () => {
    // Defensive check: ensure we have required data
    if (!documentId || !documentName) {
      console.error('[Citation] Missing required data', {
        displayIndex,
        originalIndex,
        documentId,
        documentName,
      })
      return
    }

    const precise = toPrecisePreviewHighlight(
      sourceHighlightData,
      quote ?? null,
      `citation-${documentId}-${originalIndex}`
    )

    const targetPage = resolveCitationTargetPage(
      precise?.highlight ? { highlight: precise.highlight } : undefined,
      pageNumber,
    )

    void usePreviewStore.getState().openPreview({
      documentId,
      documentName,
      ...(targetPage != null && { page: targetPage }),
      context: 'chat',
      citationLabel: `Source [${displayIndex}]`,
      ...(precise?.highlight ? { highlight: precise.highlight } : {}),
      ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
    })
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            className="inline-flex items-center justify-center text-[10px] font-semibold text-[hsl(var(--ai-accent-text))] bg-[hsl(var(--brand-soft))] hover:bg-[hsl(var(--brand-soft-strong))] rounded-full ml-0.5 px-1 py-0.5 align-super leading-none transition-colors cursor-pointer"
            aria-label={`Source ${displayIndex}: ${documentName}`}
          >
            {displayIndex}  {/* Show sequential display number */}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px] bg-popover text-popover-foreground border shadow-md">
          <p className="text-xs font-medium">{documentName}</p>
          {pageNumber ? (
            <p className="text-meta">Page {pageNumber}</p>
          ) : null}
          {(quote ?? snippet) && (
            <p className="text-meta mt-1 line-clamp-3">
              &ldquo;{quote ?? snippet}&rdquo;
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
